// THE SHARED CLIP LIBRARY — animations ANY body can play (2026-10-28).
//
// Khari: "I would like for all of our avatars to get proper animations so they
// can perform different activities in the world."
//
// Every body comes out of its own rig run, so until now a clip only played on
// the body it was rigged with. These clips carry their own rig's rest pose in
// their GLB, and `three/clipRetarget.js` replays each one as a CHANGE from that
// rest onto whichever body loads it — so one copy serves the whole cast and no
// avatar is ever re-rigged to learn something new (API
// docs/3d_body_renewal/FINDINGS.md §10, animation_slate.md).
//
// ⚠ THE FOLDER IS STAMPED. The asset proxy serves `immutable` with a one-year
// cache, so a clip that changes must change PATH: a new upload is a new stamp.
// ⚠ Load is lazy and cached per name: a body asks for an activity, the clip is
// fetched once, and every later body reuses the same parsed file.
//
// ⚠ WHICH MOVES EXIST is config/moveLibrary.js (a guarded copy of the Web's
// table, 2026-11-04): the 307 bought moves, this first set, and the recorded
// performance packs (perf_vocal_*, perf_guitar_*, perf_drum_*, play_*). Any of
// them can be an `activity` or an `?act=`. The map below only keeps Khari's
// 2026-10-28 notes on who the first set is for.
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { assetUrl } from './assets';
import { isMove, movePath, MOVES } from '../config/moveLibrary';

/* role → who it is FOR (Khari's notes, 2026-10-28).
   ⚠ The four text-to-motion drafts (rap_inplace, rap_moving, sing_ballad, hype_man)
   are GONE from here and from the table. Khari, 2026-11-04: *"we should remove the
   handwritten ones so they are not used."* Rap and singing are the recorded
   perf_vocal_* moves now. */
export const CLIP_LIBRARY = {
  // NPC life
  stand_and_drink: { role: 'npc', label: 'Stand and drink' },
  sit_and_drink: { role: 'npc_seated', label: 'Sit and drink' },
  // stage — female performers
  boom_dance: { role: 'stage_female', label: 'Boom dance' },
  bass_beats: { role: 'stage_female', label: 'Bass beats' },
  // crowd
  hip_hop_dance_1: { role: 'crowd', label: 'Hip-hop dance' },
  joyful_dance_with_hand_sway: { role: 'crowd', label: 'Joyful dance' },
  // talking
  talk_passionately: { role: 'talk', label: 'Talk passionately' },
  talk_with_hands_open: { role: 'talk', label: 'Talk, hands open' },
  talk_with_right_hand_open: { role: 'talk', label: 'Talk, right hand' },
  talk_with_left_hand_on_hip: { role: 'talk', label: 'Talk, hand on hip' },
  shrug: { role: 'react', label: 'Shrug' },
  // happy / greeting
  big_wave_hello: { role: 'greet', label: 'Big wave' },
  cheer: { role: 'happy', label: 'Cheer' },
  // sitting
  chair_sit_idle_f: { role: 'seated', label: 'Sit (f)' },
  chair_sit_idle_m: { role: 'seated', label: 'Sit (m)' },
  stand_to_sit_transition_m: { role: 'seated', label: 'Sit down', once: true },
  walk_to_sit: { role: 'seated', label: 'Walk to sit', once: true },
  // idle
  listening_gesture: { role: 'idle', label: 'Listening' },
  idle_3: { role: 'idle', label: 'Idle 3' },
  idle_9: { role: 'idle', label: 'Idle 9' },
};

export const clipFileUrl = (name) => (isMove(name) ? assetUrl(movePath(name)) : null);

const cache = new Map();
/** Resolves `{ clip, root }` — the raw clip and its rig's rest (the GLB scene). Retarget per body. */
export function loadLibraryClip(name) {
  const url = clipFileUrl(name);
  if (!url) return Promise.reject(new Error(`no clip "${name}" in the library`));
  if (!cache.has(name)) {
    cache.set(name, new GLTFLoader().loadAsync(url).then((g) => {
      if (!g.animations?.length) throw new Error(`clip "${name}" has no animation`);
      return { clip: g.animations[0], root: g.scene };
    }).catch((e) => { cache.delete(name); throw e; }));
  }
  return cache.get(name);
}

/* ── ADMIN: make residents perform without touching the city's own logic ──
   ?act=boom_dance              every body
   ?act=64:boom_dance,99:perf_vocal_04,*:hip_hop_dance_1   per avatar, `*` = everyone else
   The World is admin-facing for this; the stage and the Living City will set
   `activity` themselves once they choose performances. */
let parsed = null;
export function activityOverride(avatarId) {
  if (parsed == null) {
    parsed = {};
    try {
      const raw = new URLSearchParams(window.location.search).get('act');
      for (const part of (raw || '').split(',').map((s) => s.trim()).filter(Boolean)) {
        const [who, what] = part.includes(':') ? part.split(':') : ['*', part];
        if (isMove(what)) parsed[who] = what;
        else console.warn(`[clips] ?act: unknown move "${what}" — ${MOVES.length} are known (config/moveLibrary.js)`);
      }
    } catch { /* no window: no override */ }
  }
  return (avatarId != null && parsed[String(avatarId)]) || parsed['*'] || null;
}
